// ----------------------------------------------------------------------------------------------------
// Root application router configured with HashRouter (#) for client-side routing without server setup.
// This file wires both "user" and "carrier" realms, their auth providers, and all route guards.
// Key features applied here:
//
// 1) Has a dedicated entry route: "/carrier-inviting"
//    - Decides between "/carrier/login" and "/carrier/home" based on carrier auth state.
//
// 2) Using of "#/..." inside <Navigate to="..."> destinations.
//    - With HashRouter, React Router injects the "#" automatically.
//    - Manually adding "#" inside "to" creates malformed URLs like "#/login#/home".
//
// 3) (Optional convenience) Has an alias "/carrier" -> "/carrier-inviting"
//    - If someone opens "#/carrier" directly, they’ll still hit the entry logic.
//
// Notes:
// - RealmTracker persists which realm is active by inspecting the current path.
// - UserAuthProvider and CarrierAuthProvider are wrapped at the top so guards can use their hooks.
// - Page404 is lazy-loaded to keep the initial bundle small.
// ----------------------------------------------------------------------------------------------------

import "./App.css";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import LoginPage from "./pages/loginPage";
import HomePage from "./pages/homePage";
import CarrierInviteEntryPage from "./pages/CarrierInvitation/CarrierInviteEntryPage";

// Carrier-specific pages (login + home that wraps TabsHomePage)
import CarrierLoginPage from "./pages/CarrierInvitation/CarrierLoginPage";
import CarrierHomePage from "./pages/CarrierInvitation/CarrierHomePage";

import { MRouter, LoadingBar } from "./M_Components";
import { DefaultURl } from "./M_Components/M_Utils/url.service"; // <- safest (same exported `let`)
import i18n from "./i18next";

// Storage migration + realm tracking
import { runStorageMigrationOnce } from "./components/auth/storage";
import RealmTracker from "./components/auth/RealmTracker";

// Auth providers (user + carrier)
// We also import the carrier hook to implement the entry decision component below.
import UserAuthProvider from "./components/auth/UserAuthProvider";
import CarrierAuthProvider, { useCarrierAuth } from "./components/auth/CarrierAuthProvider";

// Route guards (user + carrier)
import {
  UserProtectedRoute,
  UserPublicOnlyRoute,
  CarrierProtectedRoute,
  CarrierPublicOnlyRoute,
} from "./components/auth/guards";

// Runtime app settings (frontend "appsettings.json") + URL application
import { loadAppSettings } from "./M_Components/M_Utils/appSettings";
import { applyConfigToDefaultUrl, applyConfigToMapSettings } from "./M_Components/M_Utils/url.service";

// Lazy 404 to keep initial bundle small
const Page404 = lazy(() => import("./pages/Page404"));

/**
 * Small helper component used in "/carrier-inviting".
 * It chooses where to navigate:
 *  - If a carrier is authenticated -> go to "/carrier/home"
 *  - Otherwise  -> go to "/carrier/login"
 *
 * We keep it here to avoid leaking auth decisions into App routes
 * and to keep redirection logic declarative.
 */
function CarrierEntry() {
  const { isAuthenticated, loading } = useCarrierAuth();

  // While auth is hydrating from storage, show a tiny loader to avoid flicker.
  if (loading) return <LoadingBar />;

  // Decide target based on current auth state.
  return isAuthenticated ? (
    <Navigate to="/carrier/home" replace />
  ) : (
    <Navigate to="/carrier/login" replace />
  );
}

export default function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      // 1) Run storage migration (ex: legacy "token" -> realm-scoped keys).
      runStorageMigrationOnce();

      // 2) Initialize i18n from persisted language preference (if any).
      const storedLang = localStorage.getItem("lng");
      if (storedLang) {
        i18n.changeLanguage(storedLang);
      }

      // 3) Load runtime app settings from /appsettings.json
      await loadAppSettings();

      // 4) Apply loaded settings to DefaultURl (keeps same public API for MRouter)
      applyConfigToDefaultUrl();

      // 4.1) Apply loaded settings to ESRI runtime variables
      applyConfigToMapSettings();

      // 5) Mark app as ready to render routes and MRouter
      setReady(true);
    };

    init();
  }, []);

  // While configuration (and other startup tasks) are loading, show the existing loading bar.
  if (!ready) {
    return <LoadingBar />;
  }

  return (
    <MRouter
      // Theme colors injected into the shared layout/components.
      primaryColor={"var(--color-red)"}
      secondaryColor={"var(--color-red-dark)"}
      tertiaryColor={"var(--color-red-light)"}
      endpoints={["booking", "bl"]}
      url={DefaultURl}
      Routing={
        <Suspense fallback={<LoadingBar />}>
          <Router>
            {/* Keep localStorage "active realm" in sync with the current URL prefix. */}
            <RealmTracker />

            {/* ----------------------------------------------------------------
                Auth Providers for both realms (user + carrier).
                Wrapping here ensures guards and pages across the tree can
                consume the right auth context.
               ---------------------------------------------------------------- */}
            <UserAuthProvider>
              <CarrierAuthProvider>
                <Routes>
                  {/* ========================= CARRIER ENTRY =========================
                          Opening route that decides where to go based on carrier auth state.
                      ================================================================== */}
                  <Route path="/carrier-inviting" element={<CarrierEntry />} />

                  {/* Optional alias, has the same behaviour as carrier inviting */}
                  <Route path="/carrier" element={<Navigate to="/carrier-inviting" replace />} />

                  {/* ========================= CARRIER PUBLIC ======================== */}
                  <Route path="/carrier/invite" element={<CarrierInviteEntryPage />} />

                  {/* Only show login if NOT authenticated */}
                  <Route
                    path="/carrier/login"
                    element={
                      <CarrierPublicOnlyRoute>
                        <CarrierLoginPage />
                      </CarrierPublicOnlyRoute>
                    }
                  />

                  {/* ========================= CARRIER PROTECTED ===================== */}
                  <Route
                    path="/carrier/home/*"
                    element={
                      <CarrierProtectedRoute>
                        <CarrierHomePage />
                      </CarrierProtectedRoute>
                    }
                  />

                  {/* =========================== USER REALM ========================== */}
                  <Route path="/" element={<Navigate to="/home" replace />} />

                  <Route
                    path="/login"
                    element={
                      <UserPublicOnlyRoute>
                        <LoginPage />
                      </UserPublicOnlyRoute>
                    }
                  />

                  <Route
                    path="/home/*"
                    element={
                      <UserProtectedRoute>
                        <HomePage />
                      </UserProtectedRoute>
                    }
                  />

                  {/* ============================= FALLBACK ========================== */}
                  <Route path="*" element={<Page404 />} />
                </Routes>
              </CarrierAuthProvider>
            </UserAuthProvider>
          </Router>
        </Suspense>
      }
    />
  );
}
